"use client";

import {
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Variants,
} from "motion/react";
import type { ReactNode } from "react";

export const subtleSpring = {
  damping: 24,
  stiffness: 260,
  type: "spring",
} as const;

export const subtleTween = {
  duration: 0.18,
  ease: "easeOut",
} as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.045,
    },
  },
};

export const panelOverlay: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

interface MotionPrimitiveProps extends Omit<HTMLMotionProps<"div">, "children"> {
  readonly children: ReactNode;
  readonly delay?: number;
}

interface MotionListItemProps extends MotionPrimitiveProps {
  readonly layout?: boolean | "position" | "size";
}

export function MotionSection({
  children,
  className,
  delay = 0,
  ...props
}: MotionPrimitiveProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      animate="visible"
      className={className}
      initial="hidden"
      {...props}
      transition={{
        ...subtleTween,
        delay: shouldReduceMotion ? 0 : delay,
        duration: shouldReduceMotion ? 0.01 : subtleTween.duration,
      }}
      variants={shouldReduceMotion ? fadeIn : fadeUp}
    >
      {children}
    </motion.div>
  );
}

export function MotionList({
  children,
  className,
  ...props
}: MotionPrimitiveProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      animate="visible"
      className={className}
      initial="hidden"
      {...props}
      transition={{ duration: shouldReduceMotion ? 0.01 : subtleTween.duration }}
      variants={shouldReduceMotion ? fadeIn : staggerContainer}
    >
      {children}
    </motion.div>
  );
}

export function MotionListItem({
  children,
  className,
  layout,
  ...props
}: MotionListItemProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      exit="exit"
      layout={shouldReduceMotion ? false : layout}
      {...props}
      transition={{
        ...subtleTween,
        layout: { duration: shouldReduceMotion ? 0.01 : 0.18, ease: "easeOut" },
      }}
      variants={shouldReduceMotion ? fadeIn : fadeUp}
    >
      {children}
    </motion.div>
  );
}

export function useSubtleInteraction() {
  const shouldReduceMotion = useReducedMotion();

  return {
    transition: subtleTween,
    whileHover: shouldReduceMotion ? undefined : { y: -2 },
    whileTap: shouldReduceMotion ? undefined : { scale: 0.99 },
  };
}

export function usePressedInteraction() {
  const shouldReduceMotion = useReducedMotion();

  return {
    transition: subtleTween,
    whileTap: shouldReduceMotion ? undefined : { scale: 0.98 },
  };
}
